import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Mail, Key, AlertCircle, CheckCircle } from 'lucide-react';
import QRCodeModal from '../../common/QRCodeModal';
import PhoneVerificationModal from '../../common/PhoneVerificationModal';
import BackupCodesModal from '../../common/BackupCodesModal';
import OTPVerificationModal from '../../common/OTPVerificationModal';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../../utils/api';
import TrustedDevices from './TrustedDevices';

function SecuritySettings() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 2FA State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
  const [secondaryMethod, setSecondaryMethod] = useState(null);
  const [secondaryDestinationMask, setSecondaryDestinationMask] = useState(null);

  // Modals
  const [showQRModal, setShowQRModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);

  // QR Code Data
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);

  // Selected method for primary setup / change
  const [selectedMethod, setSelectedMethod] = useState('');

  // Secondary (backup) method setup state
  const [showAddSecondaryPicker, setShowAddSecondaryPicker] = useState(false);
  const [secondarySetupMethod, setSecondarySetupMethod] = useState('');

  // Fetch 2FA status on mount
  useEffect(() => {
    fetchTwoFactorStatus();
  }, []);

  const fetchTwoFactorStatus = async () => {
    try {
      const response = await apiGet('/two_factor/status');
      const data = await response.json();

      if (response.ok) {
        setTwoFactorEnabled(data.two_factor_enabled);
        setTwoFactorMethod(data.two_factor_method);
        setPhoneVerified(data.phone_verified);
        setBackupCodesRemaining(data.backup_codes_remaining);
        setSecondaryMethod(data.secondary_method);
        setSecondaryDestinationMask(data.secondary_destination_mask);
      }
    } catch (err) {
      console.error('Error fetching 2FA status:', err);
    }
  };

  const handleSetup2FA = async (method) => {
    setLoading(true);
    setError('');
    setSelectedMethod(method);

    try {
      const response = await apiPost('/two_factor/setup', { method });
      const data = await response.json();

      if (response.ok) {
        if (method === 'totp') {
          setQrCode(data.qr_code);
          setTotpSecret(data.secret);
          setShowQRModal(true);
        } else if (method === 'sms') {
          if (!phoneVerified) {
            setError('Please verify your phone number first');
            setShowPhoneModal(true);
          } else {
            setSuccess('Verification code sent to your phone');
            setShowOTPModal(true);
          }
        } else if (method === 'email') {
          setSuccess('Verification code sent to your email');
          setShowOTPModal(true);
        }
      } else {
        setError(data.error || 'Failed to setup 2FA');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async (method, code) => {
    setLoading(true);
    setError('');

    try {
      const response = await apiPost('/two_factor/enable', { method, otp_code: code });
      const data = await response.json();

      if (response.ok) {
        setBackupCodes(data.backup_codes);
        setShowBackupCodesModal(true);
        setTwoFactorEnabled(true);
        setTwoFactorMethod(method);
        setSuccess('2FA enabled successfully!');
        fetchTwoFactorStatus();
      } else {
        const error = new Error(data.error || 'Failed to enable 2FA');
        setError(error.message);
        throw error; // Re-throw so modal can catch it
      }
    } catch (err) {
      setError(err.message);
      throw err; // Re-throw so modal can catch it
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    const password = prompt('Enter your password to disable 2FA:');
    if (!password) return;

    setLoading(true);
    setError('');

    try {
      const response = await apiPost('/two_factor/disable', { password });
      const data = await response.json();

      if (response.ok) {
        setTwoFactorEnabled(false);
        setTwoFactorMethod(null);
        setSuccess('2FA disabled successfully');
        fetchTwoFactorStatus();
      } else {
        setError(data.error || 'Failed to disable 2FA');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    const password = prompt('Enter your password to regenerate backup codes:');
    if (!password) return;

    setLoading(true);
    setError('');

    try {
      const response = await apiPost('/two_factor/backup_codes/regenerate', { password });
      const data = await response.json();

      if (response.ok) {
        setBackupCodes(data.backup_codes);
        setShowBackupCodesModal(true);
        setSuccess('Backup codes regenerated successfully');
        fetchTwoFactorStatus();
      } else {
        setError(data.error || 'Failed to regenerate backup codes');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await apiPost('/two_factor/setup', { method: selectedMethod });
    } catch (err) {
      throw new Error('Failed to resend code');
    }
  };

  const handleChangeMethod = async (method) => {
    // Check if SMS method requires phone verification first
    if (method === 'sms' && !phoneVerified) {
      setError('Please verify your phone number first to use SMS 2FA');
      setShowPhoneModal(true);
      return;
    }

    setLoading(true);
    setError('');
    setSelectedMethod(method);

    try {
      const response = await apiPatch('/two_factor/change_method', { method });
      const data = await response.json();

      if (response.ok) {
        if (method === 'totp') {
          setQrCode(data.qr_code);
          setTotpSecret(data.secret);
          setShowQRModal(true);
        } else if (method === 'sms') {
          setSuccess('Verification code sent to your phone');
          setShowOTPModal(true);
        } else if (method === 'email') {
          setSuccess('Verification code sent to your email');
          setShowOTPModal(true);
        }
      } else {
        setError(data.error || 'Failed to change 2FA method');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // --- Secondary (backup) method handlers ---

  const handleAddSecondary = async (method) => {
    if (method === twoFactorMethod) {
      setError('Backup method must differ from your primary method');
      return;
    }
    if (method === 'sms' && !phoneVerified) {
      setError('Please verify your phone number first');
      setShowPhoneModal(true);
      return;
    }

    setLoading(true);
    setError('');
    setSecondarySetupMethod(method);
    setShowAddSecondaryPicker(false);

    try {
      const response = await apiPost('/two_factor/secondary/setup', { method });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Failed to set up backup method');
        setSecondarySetupMethod('');
        return;
      }
      if (method === 'totp') {
        setQrCode(data.qr_code);
        setTotpSecret(data.secret);
        setShowQRModal(true);
      } else {
        setSuccess(data.message || 'Verification code sent');
        setShowOTPModal(true);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setSecondarySetupMethod('');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableSecondary = async (method, code) => {
    setLoading(true);
    setError('');
    try {
      const response = await apiPost('/two_factor/secondary/enable', { method, otp_code: code });
      const data = await response.json();
      if (response.ok) {
        setSuccess('Backup 2FA method added');
        setSecondarySetupMethod('');
        await fetchTwoFactorStatus();
      } else {
        const err = new Error(data.error || 'Failed to add backup method');
        setError(err.message);
        throw err; // OTPVerificationModal catches this to keep the modal open
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteSecondary = async () => {
    const password = prompt('Enter your password to promote your backup method to primary:');
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiPost('/two_factor/secondary/promote', { password });
      const data = await response.json();
      if (response.ok) {
        setSuccess('Backup method promoted to primary');
        await fetchTwoFactorStatus();
      } else {
        setError(data.error || 'Failed to promote backup method');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSecondary = async () => {
    const password = prompt('Enter your password to remove your backup method:');
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiDelete('/two_factor/secondary', {
        body: JSON.stringify({ password })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess('Backup method removed');
        await fetchTwoFactorStatus();
      } else {
        setError(data.error || 'Failed to remove backup method');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'totp':
        return <Smartphone className="h-5 w-5" />;
      case 'sms':
        return <Smartphone className="h-5 w-5" />;
      case 'email':
        return <Mail className="h-5 w-5" />;
      default:
        return <Shield className="h-5 w-5" />;
    }
  };

  const getMethodLabel = (method) => {
    switch (method) {
      case 'totp':
        return 'Authenticator App';
      case 'sms':
        return 'SMS';
      case 'email':
        return 'Email';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center space-x-2 mb-4">
        <Shield className="h-6 w-6 text-blue-500" />
        <h2 className="text-xl font-semibold">Security</h2>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 p-3 rounded-lg flex items-start space-x-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400 p-3 rounded-lg flex items-start space-x-2">
          <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* 2FA Status */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium">Two-Factor Authentication</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {twoFactorEnabled
                ? `Enabled via ${getMethodLabel(twoFactorMethod)}`
                : 'Add an extra layer of security to your account'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {twoFactorEnabled ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                {getMethodIcon(twoFactorMethod)}
                <span className="ml-1">Active</span>
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                Disabled
              </span>
            )}
          </div>
        </div>

        {!twoFactorEnabled ? (
          <>
            {/* Method Selection */}
            <div>
              <h4 className="text-sm font-medium mb-3">Choose your 2FA method:</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* TOTP */}
                <button
                  onClick={() => handleSetup2FA('totp')}
                  disabled={loading}
                  className="flex flex-col items-center p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Smartphone className="h-8 w-8 text-blue-500 mb-2" />
                  <span className="font-medium">Authenticator App</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                    Use Google Authenticator or similar
                  </span>
                </button>

                {/* SMS */}
                <button
                  onClick={() => handleSetup2FA('sms')}
                  disabled={loading}
                  className="flex flex-col items-center p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Smartphone className="h-8 w-8 text-green-500 mb-2" />
                  <span className="font-medium">SMS</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                    Receive codes via text message
                  </span>
                </button>

                {/* Email */}
                <button
                  onClick={() => handleSetup2FA('email')}
                  disabled={loading}
                  className="flex flex-col items-center p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Mail className="h-8 w-8 text-purple-500 mb-2" />
                  <span className="font-medium">Email</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                    Receive codes via email
                  </span>
                </button>
              </div>
            </div>

            {/* Phone Verification Notice */}
            {!phoneVerified && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-400 mb-2">
                  Verify your phone to use SMS for 2FA, primary or backup. You can verify it now and decide later.
                </p>
                <button
                  onClick={() => setShowPhoneModal(true)}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Verify Phone Number →
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Backup Codes */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">Backup Codes</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {backupCodesRemaining} codes remaining
                </p>
              </div>
              <button
                onClick={handleRegenerateBackupCodes}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
              >
                <Key className="h-4 w-4" />
                <span>Regenerate</span>
              </button>
            </div>

            {/* Phone Verification Notice (when 2FA is enabled but phone not verified) */}
            {!phoneVerified && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-400 mb-2">
                  Verify your phone to add SMS as a backup 2FA method. You don't have to add it now, but verifying keeps the option open.
                </p>
                <button
                  onClick={() => setShowPhoneModal(true)}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Verify Phone Number →
                </button>
              </div>
            )}

            {/* Backup (secondary) method */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium">Backup method</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {secondaryMethod
                      ? `${getMethodLabel(secondaryMethod)}${secondaryDestinationMask ? ` (${secondaryDestinationMask})` : ''}`
                      : 'A second way to verify if your primary method is unavailable.'}
                  </p>
                </div>
                {!secondaryMethod && !showAddSecondaryPicker && (
                  <button
                    onClick={() => setShowAddSecondaryPicker(true)}
                    disabled={loading}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
                  >
                    + Add a backup method
                  </button>
                )}
              </div>

              {!secondaryMethod && showAddSecondaryPicker && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <button
                    onClick={() => handleAddSecondary('totp')}
                    disabled={loading || twoFactorMethod === 'totp'}
                    className="relative flex flex-col items-center p-4 border-2 rounded-lg transition-colors border-gray-200 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {twoFactorMethod === 'totp' && (
                      <span className="absolute top-1 right-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-600 text-white">Primary</span>
                    )}
                    <Smartphone className="h-8 w-8 text-blue-500 mb-2" />
                    <span className="font-medium text-sm">Authenticator App</span>
                  </button>
                  <button
                    onClick={() => handleAddSecondary('sms')}
                    disabled={loading || twoFactorMethod === 'sms'}
                    className="relative flex flex-col items-center p-4 border-2 rounded-lg transition-colors border-gray-200 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {twoFactorMethod === 'sms' && (
                      <span className="absolute top-1 right-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-600 text-white">Primary</span>
                    )}
                    <Smartphone className="h-8 w-8 text-green-500 mb-2" />
                    <span className="font-medium text-sm">SMS</span>
                  </button>
                  <button
                    onClick={() => handleAddSecondary('email')}
                    disabled={loading || twoFactorMethod === 'email'}
                    className="relative flex flex-col items-center p-4 border-2 rounded-lg transition-colors border-gray-200 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {twoFactorMethod === 'email' && (
                      <span className="absolute top-1 right-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-600 text-white">Primary</span>
                    )}
                    <Mail className="h-8 w-8 text-purple-500 mb-2" />
                    <span className="font-medium text-sm">Email</span>
                  </button>
                </div>
              )}

              {secondaryMethod && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <button
                    onClick={handlePromoteSecondary}
                    disabled={loading}
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Promote to primary
                  </button>
                  <button
                    onClick={handleRemoveSecondary}
                    disabled={loading}
                    className="px-3 py-1.5 text-sm border border-red-300 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* Change 2FA Method */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="font-medium mb-3">Change 2FA Method</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Currently using: <span className="font-medium text-gray-900 dark:text-white">{getMethodLabel(twoFactorMethod)}</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* TOTP. Current method shows as a non-clickable "Current" tile. */}
                <button
                  onClick={() => handleChangeMethod('totp')}
                  disabled={loading || twoFactorMethod === 'totp' || secondaryMethod === 'totp'}
                  className={`relative flex flex-col items-center p-4 border-2 rounded-lg transition-colors ${
                    twoFactorMethod === 'totp'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-default'
                      : secondaryMethod === 'totp'
                        ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 cursor-not-allowed'
                        : 'border-gray-200 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                  title={secondaryMethod === 'totp' ? 'Already your backup method. Promote or remove it first.' : undefined}
                >
                  {twoFactorMethod === 'totp' && (
                    <span className="absolute top-1 right-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-600 text-white">Current</span>
                  )}
                  {secondaryMethod === 'totp' && twoFactorMethod !== 'totp' && (
                    <span className="absolute top-1 right-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-500 text-white">Backup</span>
                  )}
                  <Smartphone className="h-8 w-8 text-blue-500 mb-2" />
                  <span className="font-medium text-sm">Authenticator App</span>
                </button>

                {/* SMS */}
                <button
                  onClick={() => handleChangeMethod('sms')}
                  disabled={loading || twoFactorMethod === 'sms' || secondaryMethod === 'sms'}
                  className={`relative flex flex-col items-center p-4 border-2 rounded-lg transition-colors ${
                    twoFactorMethod === 'sms'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 cursor-default'
                      : secondaryMethod === 'sms'
                        ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 cursor-not-allowed'
                        : 'border-gray-200 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                  title={secondaryMethod === 'sms' ? 'Already your backup method. Promote or remove it first.' : undefined}
                >
                  {twoFactorMethod === 'sms' && (
                    <span className="absolute top-1 right-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-600 text-white">Current</span>
                  )}
                  {secondaryMethod === 'sms' && twoFactorMethod !== 'sms' && (
                    <span className="absolute top-1 right-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-500 text-white">Backup</span>
                  )}
                  <Smartphone className="h-8 w-8 text-green-500 mb-2" />
                  <span className="font-medium text-sm">SMS</span>
                </button>

                {/* Email */}
                <button
                  onClick={() => handleChangeMethod('email')}
                  disabled={loading || twoFactorMethod === 'email' || secondaryMethod === 'email'}
                  className={`relative flex flex-col items-center p-4 border-2 rounded-lg transition-colors ${
                    twoFactorMethod === 'email'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 cursor-default'
                      : secondaryMethod === 'email'
                        ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 cursor-not-allowed'
                        : 'border-gray-200 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                  title={secondaryMethod === 'email' ? 'Already your backup method. Promote or remove it first.' : undefined}
                >
                  {twoFactorMethod === 'email' && (
                    <span className="absolute top-1 right-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-600 text-white">Current</span>
                  )}
                  {secondaryMethod === 'email' && twoFactorMethod !== 'email' && (
                    <span className="absolute top-1 right-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-500 text-white">Backup</span>
                  )}
                  <Mail className="h-8 w-8 text-purple-500 mb-2" />
                  <span className="font-medium text-sm">Email</span>
                </button>
              </div>
            </div>

            {/* Disable 2FA */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleDisable2FA}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
              >
                Disable 2FA
              </button>
            </div>
          </>
        )}

        <TrustedDevices />
      </div>

      {/* Modals */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => {
          setShowQRModal(false);
          // Cancelling QR mid-secondary-enrollment must clear secondarySetupMethod;
          // otherwise the shared OTPVerificationModal would route a later primary
          // setup's code to /two_factor/secondary/enable.
          setSecondarySetupMethod('');
        }}
        qrCode={qrCode}
        secret={totpSecret}
        onVerify={(code) =>
          secondarySetupMethod
            ? handleEnableSecondary('totp', code)
            : handleEnable2FA('totp', code)
        }
      />

      <PhoneVerificationModal
        isOpen={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        onSuccess={() => {
          setPhoneVerified(true);
          setSuccess('Phone verified successfully!');
          fetchTwoFactorStatus();
        }}
      />

      <BackupCodesModal
        isOpen={showBackupCodesModal}
        onClose={() => setShowBackupCodesModal(false)}
        backupCodes={backupCodes}
      />

      <OTPVerificationModal
        isOpen={showOTPModal}
        onClose={() => {
          setShowOTPModal(false);
          setSuccess('');
          setSecondarySetupMethod('');
        }}
        method={secondarySetupMethod || selectedMethod}
        onVerify={(code) =>
          secondarySetupMethod
            ? handleEnableSecondary(secondarySetupMethod, code)
            : handleEnable2FA(selectedMethod, code)
        }
        onResend={async () => {
          const response = secondarySetupMethod
            ? await apiPost('/two_factor/secondary/setup', { method: secondarySetupMethod })
            : await apiPost('/two_factor/setup', { method: selectedMethod });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(data.error || 'Failed to resend verification code.');
          }
        }}
      />
    </div>
  );
}

export default SecuritySettings;
